import React from 'react';
import {
  AdjustmentsHorizontalIcon,
  TrashIcon,
  DocumentTextIcon,
  SparklesIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';

const BulkActions = ({
  showBulkActions,
  setShowBulkActions,
  selectedUsers,
  selectedBulkDeleteUsers,
  selectedGroups,
  selectedCrews,
  groups,
  crews,
  pendingRequests,
  trainingSessions,
  autoplannerTasks,
  users,
  currentUserId,
  handleSelectAll,
  handleSelectItem,
  handleBulkDeleteSessions,
  handleBulkAcceptUsers,
  handleBulkDeleteUsers,
  handleBulkDeleteTasks,
  handleBulkDeleteCrews,
  handleBulkRemoveUsersFromGroup,
  bulkDeleteSessionsMutation,
  bulkAcceptUsersMutation,
  bulkDeleteUsersMutation,
  bulkDeleteTasksMutation,
  bulkDeleteCrewsMutation,
  bulkActionType,
}) => {
  // Calculate filtered counts
  // Sessions are linked to training groups through crew assignments
  const sessionsForSelectedGroups = trainingSessions.filter((session) => {
    // Check if any of the session's crew assignments have crews that belong to selected training groups
    return session.crew_assignments?.some(
      (crewAssignment) =>
        crewAssignment.crew?.training_group_id &&
        selectedGroups.has(crewAssignment.crew.training_group_id)
    );
  });

  // Tasks are linked to training groups directly
  const tasksForSelectedGroups = autoplannerTasks.filter(
    (task) => task.training_group?.id && selectedGroups.has(task.training_group.id)
  );

  const usersEligibleForBulkDelete = users.filter((u) => u.id !== currentUserId);

  return (
    <section className="rounded-lg border border-orange-200 bg-orange-50 p-6 shadow-sm">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-orange-900">Bulk Actions</h2>
          <p className="mt-1 text-sm text-orange-700">Perform bulk operations on multiple items</p>
        </div>
        <button
          className="flex items-center gap-2 rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
          onClick={() => setShowBulkActions(!showBulkActions)}
        >
          <AdjustmentsHorizontalIcon className="h-4 w-4" />
          {showBulkActions ? 'Hide' : 'Show'} Bulk Actions
        </button>
      </div>

      {showBulkActions && (
        <div className="space-y-6">
          {/* Bulk Session Management */}
          <div className="rounded-lg border border-orange-200 bg-white p-4">
            <h3 className="mb-3 text-lg font-medium text-orange-900">Bulk Session Management</h3>
            <div className="mb-4">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Select Training Groups:</span>
                <button
                  className="text-xs text-orange-600 hover:text-orange-700"
                  onClick={() => handleSelectAll('groups', groups)}
                >
                  Select All ({groups.length})
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-4">
                {groups.map((group) => (
                  <label key={group.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={selectedGroups.has(group.id)}
                      onChange={() => handleSelectItem('groups', group.id)}
                      className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                    />
                    <span className="text-sm text-gray-700">{group.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <button
                className="flex items-center gap-2 rounded-md bg-red-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                onClick={handleBulkDeleteSessions}
                disabled={selectedGroups.size === 0 || bulkDeleteSessionsMutation.isLoading}
              >
                <TrashIcon className="h-4 w-4" />
                Delete Sessions ({sessionsForSelectedGroups.length})
              </button>
              <button
                className="flex items-center gap-2 rounded-md bg-yellow-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
                onClick={handleBulkDeleteTasks}
                disabled={selectedGroups.size === 0 || bulkDeleteTasksMutation.isLoading}
              >
                <DocumentTextIcon className="h-4 w-4" />
                Delete Tasks ({tasksForSelectedGroups.length})
              </button>
            </div>
          </div>

          {/* Bulk User Management */}
          <div className="rounded-lg border border-orange-200 bg-white p-4">
            <h3 className="mb-3 text-lg font-medium text-orange-900">Bulk User Management</h3>
            <div className="mb-4">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Select Pending Users:</span>
                <button
                  className="text-xs text-orange-600 hover:text-orange-700"
                  onClick={() => handleSelectAll('users', pendingRequests)}
                >
                  Select All ({pendingRequests.length})
                </button>
              </div>
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
                {pendingRequests.map((request) => (
                  <label key={request.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={selectedUsers.has(request.id)}
                      onChange={() => handleSelectItem('users', request.id)}
                      className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                    />
                    <span className="text-sm text-gray-700">
                      {request.first_name} {request.last_name} ({request.email})
                    </span>
                  </label>
                ))}
              </div>
            </div>
            <button
              className="flex items-center gap-2 rounded-md bg-green-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              onClick={handleBulkAcceptUsers}
              disabled={selectedUsers.size === 0 || bulkAcceptUsersMutation.isLoading}
            >
              <SparklesIcon className="h-4 w-4" />
              Approve Users ({selectedUsers.size})
            </button>

            <div className="mt-6 border-t border-orange-100 pt-6">
              <p className="mb-3 text-sm font-medium text-gray-800">
                Permanently delete accounts (all users except you)
              </p>
              <div className="mb-4">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Select users:</span>
                  <button
                    type="button"
                    className="text-xs text-orange-600 hover:text-orange-700"
                    onClick={() =>
                      handleSelectAll('bulk-delete-users', usersEligibleForBulkDelete)
                    }
                  >
                    Select All ({usersEligibleForBulkDelete.length})
                  </button>
                </div>
                <div className="max-h-64 overflow-y-auto rounded border border-gray-100 bg-gray-50/50 p-2">
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
                    {usersEligibleForBulkDelete.map((u) => (
                      <label key={u.id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={selectedBulkDeleteUsers.has(u.id)}
                          onChange={() => handleSelectItem('bulk-delete-users', u.id)}
                          className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                        />
                        <span className="text-sm text-gray-700">
                          {u.first_name} {u.last_name} ({u.email})
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="flex items-center gap-2 rounded-md bg-red-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                onClick={handleBulkDeleteUsers}
                disabled={
                  selectedBulkDeleteUsers.size === 0 || bulkDeleteUsersMutation.isLoading
                }
              >
                <TrashIcon className="h-4 w-4" />
                Delete selected users ({selectedBulkDeleteUsers.size})
              </button>
            </div>
          </div>

          {/* Bulk Crew Management */}
          <div className="rounded-lg border border-orange-200 bg-white p-4">
            <h3 className="mb-3 text-lg font-medium text-orange-900">Bulk Crew Management</h3>
            <div className="mb-4">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Select Crews:</span>
                <button
                  className="text-xs text-orange-600 hover:text-orange-700"
                  onClick={() => handleSelectAll('crews', crews)}
                >
                  Select All ({crews.length})
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-4">
                {crews.map((crew) => (
                  <label key={crew.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={selectedCrews.has(crew.id)}
                      onChange={() => handleSelectItem('crews', crew.id)}
                      className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                    />
                    <span className="text-sm text-gray-700">{crew.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <button
              className="flex items-center gap-2 rounded-md bg-red-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              onClick={handleBulkDeleteCrews}
              disabled={selectedCrews.size === 0 || bulkDeleteCrewsMutation.isLoading}
            >
              <TrashIcon className="h-4 w-4" />
              Delete Crews ({selectedCrews.size})
            </button>
          </div>

          {/* Remove Users from Training Groups */}
          {bulkActionType?.startsWith('remove-from-group-') && (
            <div className="rounded-lg border border-orange-200 bg-white p-4">
              <h3 className="mb-3 text-lg font-medium text-orange-900">
                Remove Users from Training Group
              </h3>
              <div className="mb-4">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Select Users to Remove:</span>
                  <button
                    className="text-xs text-orange-600 hover:text-orange-700"
                    onClick={() => {
                      const groupId = parseInt(bulkActionType.split('-').pop());
                      const usersInGroup = users.filter((u) =>
                        u.training_groups?.some((tg) => tg.id === groupId)
                      );
                      handleSelectAll('users', usersInGroup);
                    }}
                  >
                    Select All (
                    {
                      users.filter((u) => {
                        const groupId = parseInt(bulkActionType.split('-').pop());
                        return u.training_groups?.some((tg) => tg.id === groupId);
                      }).length
                    }
                    )
                  </button>
                </div>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
                  {users
                    .filter((u) => {
                      const groupId = parseInt(bulkActionType.split('-').pop());
                      return u.training_groups?.some((tg) => tg.id === groupId);
                    })
                    .map((user) => (
                      <label key={user.id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={selectedUsers.has(user.id)}
                          onChange={() => handleSelectItem('users', user.id)}
                          className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                        />
                        <span className="text-sm text-gray-700">
                          {user.first_name} {user.last_name} ({user.email})
                        </span>
                      </label>
                    ))}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  className="flex items-center gap-2 rounded-md bg-orange-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                  onClick={() => {
                    const groupId = parseInt(bulkActionType.split('-').pop());
                    handleBulkRemoveUsersFromGroup(groupId);
                  }}
                  disabled={selectedUsers.size === 0}
                >
                  <ArrowRightOnRectangleIcon className="h-4 w-4" />
                  Remove Selected ({selectedUsers.size})
                </button>
                <button
                  className="flex items-center gap-2 rounded-md bg-gray-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  onClick={() => {
                    setShowBulkActions(false);
                    handleSelectAll('users', []);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default BulkActions;
